import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import type { ReactNode } from "react";

type BetaAccessGateProps = {
  featureKey: string;
  featureName: string;
  description?: string;
  contactEmail?: string;
  children: ReactNode;
  loadingFallback?: ReactNode;
};

/**
 * Wraps a beta-gated section. While the access query is still resolving the
 * children are not rendered (avoids a flash of the gate for users who do have
 * access). Once the query settles, users without the grant see a friendly
 * "Beta Access Required" empty state with a contact CTA; users with the grant
 * see the children. Background access checks do not show the loading fallback
 * or remount granted children; a revoked grant or lookup error closes the gate.
 */
export function BetaAccessGate({
  featureKey,
  featureName,
  description,
  contactEmail = "support@padpulse.io",
  children,
  loadingFallback = null,
}: BetaAccessGateProps) {
  const { granted, loaded } = useBetaAccessState(featureKey);

  if (!loaded) return <>{loadingFallback}</>;
  if (granted) return <>{children}</>;

  const subject = encodeURIComponent(`Beta access request: ${featureName}`);
  const body = encodeURIComponent(
    `Hi PadPulse team,\n\nI'd like to request beta access to ${featureName}.\n\nThanks!`,
  );

  return (
    <div
      className="flex flex-col items-center justify-center py-24 px-6 text-center"
      data-testid={`beta-gate-${featureKey}`}
    >
      <div className="rounded-full bg-blue-50 p-5 mb-4">
        <Lock className="h-8 w-8 text-brand" />
      </div>
      <h2
        className="text-xl font-semibold text-gray-900 mb-2"
        data-testid={`beta-gate-title-${featureKey}`}
      >
        Beta Access Required
      </h2>
      <p className="text-sm text-gray-600 max-w-md mb-1">
        <span className="font-medium text-gray-900">{featureName}</span> is
        currently in beta.
      </p>
      <p
        className="text-sm text-gray-500 max-w-md mb-6"
        data-testid={`beta-gate-description-${featureKey}`}
      >
        {description ??
          "This feature is rolling out to a small group of hosts first. Reach out and we'll get you added to the beta."}
      </p>
      <a
        href={`mailto:${contactEmail}?subject=${subject}&body=${body}`}
        data-testid={`beta-gate-contact-${featureKey}`}
      >
        <Button>Request beta access</Button>
      </a>
    </div>
  );
}
